import React, { useEffect, useMemo, useState } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { FadeUp } from '../components/MotionReveals';
import {
  Gauge, AlertTriangle, ClipboardCheck, RotateCcw, Eye, CheckCircle2,
  ChevronRight, Clock, HelpCircle, Quote, ListChecks, Route, Scale, Crosshair, Shield,
} from 'lucide-react';

// ─── Données pédagogiques Intersections & priorité à droite ───
const SCENARIOS = {
  pad: {
    tab: 'Priorité à droite (AB1)',
    icon: Crosshair,
    badge: 'Carrefour non protégé · Règle par défaut R415-5 · Le piège n°1 en agglo',
    titre: 'La priorité à droite — la règle qui s\'applique quand rien n\'est dit.',
    scene: 'pad',
    chapters: [
      { label: 'Détecter le carrefour', desc: 'Pas de panneau, pas de marquage = priorité à droite par défaut' },
      { label: 'Allure d\'approche réduite', desc: 'On descend à 30-40 km/h, pied prêt, regard dans la rue de droite' },
      { label: 'Véhicule à droite → on cède', desc: 'Il arrive par la droite : ralentir voire s\'arrêter pour le laisser passer' },
      { label: 'Traversée contrôlée', desc: 'Rue de droite dégagée : on franchit en restant prêt à s\'arrêter' },
    ],
    dangerNote: 'En agglomération, la priorité à droite est la règle PAR DÉFAUT : l\'absence de panneau ne veut pas dire que tu es prioritaire, elle veut dire que celui de droite l\'est.',
    focus: {
      txt: <>Le piège mortel : croire qu'une rue plus petite à droite « ne compte pas ». <strong className="text-white">La taille de la rue ne change rien</strong> : si aucun panneau ni marquage ne t'accorde la priorité, le véhicule venant de droite passe en premier — même s'il sort d'une ruelle. À l'inverse, une priorité à droite <strong className="text-white">se prend sans se forcer</strong> : tu vérifies que l'autre t'a vu avant de t'engager.</>,
      tags: ['Absence de panneau = priorité à droite', 'Une priorité se prend, ne se force pas'],
    },
    quiz: [
      { q: 'Carrefour en ville, aucun panneau, aucun marquage. Qui est prioritaire ?', opts: ['Celui qui est sur la rue la plus large', 'Le véhicule qui vient de droite', 'Celui qui arrive le premier'], ok: 1,
        exp: 'R415-5 : sans signalisation, la priorité à droite s\'applique. La largeur de la rue, l\'allure ou l\'ordre d\'arrivée ne comptent pas.' },
      { q: 'Le panneau AB1 (croix noire dans un triangle) signifie…', opts: ['Intersection où je suis prioritaire', 'Intersection où la priorité à droite s\'applique', 'Carrefour dangereux, priorité au plus rapide'], ok: 1,
        exp: 'AB1 = annonce d\'une intersection régie par la priorité à droite. Il te rappelle la règle par défaut, il ne te donne aucune priorité.' },
      { q: 'Tu es prioritaire (il vient de ta gauche) mais il ne ralentit pas. Tu…', opts: ['Passes : c\'est ton droit, il freinera', 'Renonces à ta priorité et le laisses passer', 'Klaxonnes et accélères'], ok: 1,
        exp: 'Avoir raison ne protège pas d\'une collision. Une priorité se prend seulement si l\'autre montre qu\'il te l\'accorde (il ralentit, croise ton regard).' },
      { q: 'Quelle allure dans une zone de priorités à droite successives ?', opts: ['50 km/h, c\'est la limite en agglo', '30-40 km/h max, pied prêt à freiner à chaque carrefour', '20 km/h partout par précaution'], ok: 1,
        exp: 'La limite légale n\'est pas une consigne d\'allure. En zone de priorités à droite, il faut pouvoir s\'arrêter sur la distance de visibilité de chaque rue.' },
      { q: 'Un cycliste débouche de la rue de droite non signalée. Tu…', opts: ['Passes, un vélo n\'a pas la priorité à droite', 'Lui cèdes le passage : la règle vaut pour tous les véhicules', 'Le laisses passer seulement s\'il est rapide'], ok: 1,
        exp: 'Le vélo est un véhicule : la priorité à droite le protège exactement comme une voiture. Refus de priorité à un cycliste = éliminatoire + danger grave.' },
      { q: 'Visibilité nulle sur la rue de droite (mur, haie). Tu…', opts: ['Passes à allure normale : pas de visu = pas de danger prouvé', 'Avances au pas en « construisant » la visibilité, prêt à l\'arrêt total', 'Klaxonnes pour signaler ton passage et maintiens l\'allure'], ok: 1,
        exp: 'Ce qu\'on ne voit pas peut surgir. On réduit jusqu\'à l\'allure qui permet l\'arrêt sur la distance visible — parfois 10 km/h, parfois l\'arrêt.' },
    ],
    curve: [50, 48, 45, 40, 34, 30, 26, 22, 18, 15, 12, 15, 20, 26, 30, 35, 40, 44, 47, 50],
    stats: [{ v: 50, l: 'km/h approche' }, { v: '30-40', l: 'km/h carrefour' }, { v: 'Droite', l: 'regard clé' }],
    identite: {
      type: 'Carrefour non protégé — règle par défaut',
      typeDesc: 'Aucun panneau, aucun marquage : R415-5 s\'applique.',
      difficulte: 'Avancé',
      difficulteDesc: 'Rien n\'est signalé : tout repose sur ta lecture.',
      danger: 'Fausse impression de priorité',
      dangerDesc: 'Rue large + habitude = on oublie la règle.',
      chips: ['✖️ AB1 · Croix de Saint-André', '📍 Typique : agglo, lotissements, centres-villes', '⚖️ Art. R415-5 — priorité à droite par défaut'],
    },
    comprendre: <>La priorité à droite est la <strong className="text-white">règle silencieuse</strong> du Code : elle s'applique partout où rien d'autre n'est dit. L'élève doit inverser son réflexe : ne pas chercher « qui me laisse passer » mais <strong className="text-white">« à qui je dois céder »</strong>. Chaque rue à droite non signalée est une question à résoudre AVANT d'arriver dessus.</>,
    vitesseLegale: '50 → 30-40 carrefour',
    rapport: '3ème → 2ème',
    remc: 'Réf : REMC C2 — Franchir les intersections selon le régime de priorité',
    segments: [
      { time: 'Phase 1', speed: '50 → 40 km/h', title: 'Détecter le carrefour', emoji: '🔍',
        obs: ['Indices : trouée dans les façades, panneau AB1, bateau de trottoir, débouché de véhicules', 'Question réflexe : « ce carrefour est-il protégé ? » → pas de panneau = priorité à droite', 'Rétro intérieur : informer celui qui suit du ralentissement à venir', 'Pré-positionnement du regard vers l\'entrée de la rue de droite'],
        q: 'Comment je repère un carrefour non signalé ?',
        r: 'Par l\'environnement : alignement de façades qui s\'interrompt, trottoir abaissé, véhicules en attente, miroir d\'agglomération. En ville, toute rue à droite est suspecte jusqu\'à preuve du contraire (panneau ou cédez sur SA voie).',
        verdict: 'Le carrefour est identifié à 50 m : la décision se prépare, elle ne s\'improvise pas.' },
      { time: 'Phase 2', speed: '40 → 30 km/h', title: 'Allure d\'approche', emoji: '🐢',
        obs: ['Descente à 30-40 km/h : l\'allure qui permet l\'arrêt sur la distance visible', '2ème engagée, pied au-dessus du frein (pas dessus en continu)', 'Balayage : rue de droite PUIS gauche PUIS droite à nouveau', 'Détection des masques : véhicule stationné, haie, benne'],
        q: 'Pourquoi regarder la droite en premier ?',
        r: 'Parce que c\'est de droite que vient celui à qui tu dois céder : c\'est l\'information qui commande TA décision. La gauche te renseigne sur celui qui doit te céder — utile, mais secondaire.',
        verdict: '30 km/h au seuil du carrefour : les deux options (passer / s\'arrêter) restent ouvertes.' },
      { time: 'Phase 3', speed: '30 → 12 km/h', title: 'Véhicule à droite', emoji: '🚗',
        obs: ['Un véhicule aborde le carrefour par la droite : il est prioritaire, point', 'Ralentissement net et VISIBLE : lui montrer qu\'on lui cède', 'Arrêt complet si nécessaire — sans mordre dans le carrefour', 'Attention aux suivants : une priorité à droite peut en cacher une deuxième'],
        q: 'Il hésite et me fait signe de passer. Je passe ?',
        r: 'Prudence : un signe n\'est pas une garantie (il peut redémarrer, un deux-roues peut le dépasser). Si tu passes, c\'est au pas, en le regardant, prêt à t\'arrêter. À l\'examen, la réponse attendue reste : on lui laisse sa priorité.',
        verdict: 'Céder de façon lisible : l\'autre comprend ton intention sans deviner.' },
      { time: 'Phase 4', speed: '15 → 45 km/h', title: 'Traversée contrôlée', emoji: '💨',
        obs: ['Rue de droite dégagée et vue construite : on franchit sans flâner', 'Dernier contrôle droite au moment de franchir le seuil', 'Relance progressive, pas d\'accélération brutale en sortie', 'Déjà anticiper le carrefour suivant : les priorités à droite vont par séries'],
        q: 'Et si je n\'ai pas pu voir la rue de droite ?',
        r: 'Alors tu n\'as pas le droit de la franchir à cette allure. On descend encore — jusqu\'au pas s\'il le faut. L\'allure se règle sur la visibilité, jamais sur l\'habitude.',
        verdict: 'Traversée décidée, jamais subie : on sait POURQUOI on passe.' },
    ],
    pieges: [
      { t: 'La rue « qui ne compte pas »', d: 'Ruelle étroite, sortie de lotissement avec chaussée normale : si rien ne la signale comme non prioritaire, celui qui en sort a la priorité sur toi.', tag: 'Règle par défaut' },
      { t: 'La série de carrefours', d: 'Tu as gérié le premier, tu relances à 50… et le deuxième arrive à 40 m. En agglo, les priorités à droite se suivent : l\'allure se règle sur la série, pas sur un seul.', tag: 'Anticipation' },
      { t: 'Forcer sa propre priorité', d: 'Tu es prioritaire mais tu t\'engages sans vérifier que l\'autre te cède. S\'il ne t\'a pas vu, avoir raison ne t\'évitera pas le choc latéral.', tag: 'Défense' },
    ],
    conduiteModele: <>Le conducteur modèle identifie le carrefour à 50 m, descend à <strong className="text-white">30-40 km/h</strong>, interroge la rue de droite en premier, cède de façon visible quand un véhicule s'y présente, et ne prend sa propre priorité que lorsque l'autre la lui accorde clairement.</>,
    grille: [
      { l: 'Carrefour détecté et verbalisé à 50 m', c: 'Prise d\'info lointaine' },
      { l: 'Statut du carrefour identifié (protégé ou non)', c: 'Analyse' },
      { l: 'Rétro intérieur avant ralentissement', c: 'Contrôles' },
      { l: 'Allure réduite à 30-40 km/h', c: 'Gestion allure' },
      { l: 'Regard rue de droite en premier', c: 'Observation' },
      { l: 'Balayage D-G-D complet', c: 'Observation' },
      { l: 'Cède de façon visible au véhicule de droite', c: 'Règle' },
      { l: 'Vulnérables (vélos) intégrés à la règle', c: 'Vulnérable' },
      { l: 'Allure au pas si visibilité masquée', c: 'Adaptation' },
      { l: 'Sa propre priorité prise sans forcer', c: 'Défense' },
      { l: 'Carrefour suivant déjà anticipé', c: 'Anticipation' },
    ],
    phrase: '« En ville, chaque rue à droite te pose une question. Si personne n\'y a répondu par un panneau, la réponse c\'est : lui d\'abord. »',
  },
  prioritaire: {
    tab: 'Route prioritaire (AB6)',
    icon: Shield,
    badge: 'Panneau jaune AB6a · Priorité sur tout l\'itinéraire · Jusqu\'au AB6b',
    titre: 'La route prioritaire — prioritaire ne veut pas dire invincible.',
    scene: 'prioritaire',
    chapters: [
      { label: 'Panneau AB6a lu', desc: 'Carré jaune : je suis sur route à caractère prioritaire jusqu\'à nouvel ordre' },
      { label: 'Vigilance active', desc: 'Prioritaire ≠ aveugle : on surveille quand même chaque débouché' },
      { label: 'Un véhicule force le passage', desc: 'Défense : on ralentit, on cède, on ne « gagne » jamais un choc' },
      { label: 'AB6b : fin de priorité', desc: 'Carré jaune barré : retour immédiat à la priorité à droite' },
    ],
    dangerNote: 'Le panneau AB6b (fin de route prioritaire) est l\'un des plus ratés à l\'examen : après des kilomètres « protégé », l\'élève oublie que la toute prochaine intersection redevient une priorité à droite.',
    focus: {
      txt: <>Être prioritaire change tes droits, <strong className="text-white">pas tes devoirs d'observation</strong>. Sur route prioritaire tu maintiens ton allure et les autres te cèdent — mais chaque carrefour reste contrôlé du regard, car l'erreur de l'autre devient TON accident. Et au premier AB6b ou AB2a, la bulle de protection éclate : <strong className="text-white">retour instantané à la règle par défaut</strong>.</>,
      tags: ['Prioritaire = maintenir, pas foncer', 'AB6b = la bulle éclate'],
    },
    quiz: [
      { q: 'Le panneau AB6a (carré jaune sur pointe) t\'indique…', opts: ['Une priorité uniquement au prochain carrefour', 'Que tu es sur route prioritaire jusqu\'au panneau de fin (AB6b)', 'Une zone de travaux'], ok: 1,
        exp: 'AB6a = caractère prioritaire de l\'ITINÉRAIRE : valable à toutes les intersections jusqu\'à l\'AB6b (carré jaune barré). L\'AB2a, lui, ne vaut que pour le prochain carrefour.' },
      { q: 'Quelle différence entre AB2a et AB6a ?', opts: ['Aucune, ce sont deux dessins de la même règle', 'AB2a : priorité ponctuelle (un seul carrefour) · AB6a : priorité sur tout l\'itinéraire', 'AB2a est réservé aux routes nationales'], ok: 1,
        exp: 'AB2a (pointe de flèche) = prioritaire au prochain carrefour seulement. AB6a = prioritaire jusqu\'à l\'annonce de fin. Confondre les deux fausse toute la lecture d\'itinéraire.' },
      { q: 'Sur route prioritaire, un véhicule s\'engage devant toi depuis un stop. Tu…', opts: ['Maintiens ton allure : il est en tort', 'Ralentis et évites le choc, quitte à céder ta priorité', 'Fais un appel de phares et accélères'], ok: 1,
        exp: 'La priorité est un droit, pas un bouclier. R412-6 : tout conducteur doit rester constamment en mesure d\'éviter l\'accident. On cède, on ne « gagne » jamais une collision.' },
      { q: 'Tu viens de passer un AB6b. La prochaine intersection non signalée est…', opts: ['Encore protégée : la priorité s\'éteint progressivement', 'Une priorité à droite classique, dès maintenant', 'Un cédez-le-passage implicite pour les autres'], ok: 1,
        exp: 'L\'AB6b coupe net le régime prioritaire : dès le panneau, la règle par défaut (priorité à droite) reprend à la première intersection rencontrée.' },
      { q: 'En agglomération, une route prioritaire croise un passage piéton. Les piétons…', opts: ['Doivent attendre, la route est prioritaire', 'Restent prioritaires dès qu\'ils manifestent l\'intention de traverser', 'Ne peuvent traverser qu\'au feu'], ok: 1,
        exp: 'Le caractère prioritaire règle les rapports entre VÉHICULES aux intersections. Il n\'enlève rien aux piétons : R415-11, on leur cède le passage.' },
      { q: 'Sur route prioritaire, ta vigilance aux intersections doit être…', opts: ['Nulle : c\'est tout l\'intérêt du panneau', 'Maintenue : regard sur chaque débouché, prêt à réagir', 'Reportée uniquement sur les rétros'], ok: 1,
        exp: 'Statistiquement, les refus de priorité des autres se produisent... sur les axes prioritaires. Le regard contrôle chaque débouché même quand le droit est pour toi.' },
    ],
    curve: [70, 70, 70, 68, 70, 70, 65, 55, 45, 40, 48, 58, 66, 70, 70, 62, 50, 42, 38, 35],
    stats: [{ v: 70, l: 'km/h maintenu' }, { v: 'AB6b', l: 'fin de bulle' }, { v: '−30', l: 'km/h après fin' }],
    identite: {
      type: 'Itinéraire prioritaire AB6a → AB6b',
      typeDesc: 'Priorité à TOUTES les intersections de l\'itinéraire.',
      difficulte: 'Intermédiaire',
      difficulteDesc: 'Le danger est dans la transition de régime.',
      danger: 'Excès de confiance + fin de priorité ratée',
      dangerDesc: 'On fonce « protégé », on rate l\'AB6b.',
      chips: ['🟨 AB6a · Carré jaune sur pointe', '🟨⃠ AB6b · Fin de route prioritaire', '⚖️ AB2a = priorité ponctuelle (1 carrefour)'],
    },
    comprendre: <>La route prioritaire crée une <strong className="text-white">bulle de protection juridique</strong> — pas physique. L'élève doit apprendre deux choses : maintenir une allure stable et lisible tant que la bulle existe, et <strong className="text-white">détecter l'instant où elle éclate</strong> (AB6b, AB2a, feux, agglomération dense). C'est la gestion des transitions qui fait la différence à l'examen.</>,
    vitesseLegale: '70-80 maintenus',
    rapport: '4ème-5ème stables',
    remc: 'Réf : REMC C2/C3 — Adapter sa conduite au régime de priorité de l\'itinéraire',
    segments: [
      { time: 'Phase 1', speed: '70 km/h stable', title: 'Lecture AB6a', emoji: '🟨',
        obs: ['Carré jaune sur pointe : itinéraire prioritaire jusqu\'à nouvel ordre', 'Conséquence : allure stable, pas de ralentissement aux intersections', 'Les transversales ont un stop ou un cédez : on peut le vérifier d\'un coup d\'œil', 'Mémoriser le régime : « je suis en prioritaire » se verbalise'],
        q: 'Je ralentis quand même à chaque carrefour ?',
        r: 'Non — c\'est même une faute de fluidité : le régime prioritaire existe pour maintenir l\'allure. Ce qu\'on maintient AUSSI : le regard sur chaque débouché. Allure stable, vigilance active.',
        verdict: '70 km/h tenus, regard vivant : la définition de la conduite sur axe prioritaire.' },
      { time: 'Phase 2', speed: '70 km/h · regard actif', title: 'Vigilance active', emoji: '👁️',
        obs: ['Chaque débouché est scanné : véhicule en attente ? vitesse d\'approche ?', 'Indices de danger : véhicule qui ne s\'arrête pas franchement au stop adverse', 'Couverture du frein à l\'approche des carrefours masqués', 'Rétros réguliers : qui me suit, à quelle distance ?'],
        q: 'À quoi je reconnais que l\'autre va me refuser la priorité ?',
        r: 'Sa roue avant et son regard : s\'il arrive vite sur son cédez sans plonger du nez (pas de freinage marqué), ou s\'il regarde de l\'autre côté, considère qu\'il va s\'engager. Pied sur le frein, prêt.',
        verdict: 'La priorité se surveille comme si on ne l\'avait pas — on la garde juste en allure.' },
      { time: 'Phase 3', speed: '70 → 40 km/h', title: 'L\'autre force le passage', emoji: '🛡️',
        obs: ['Un véhicule s\'engage depuis la transversale malgré son cédez', 'Réaction : lever de pied + freinage dégressif, PAS de coup de volant réflexe', 'On cède notre priorité : l\'éviter vaut mieux que l\'emporter', 'Pas de sanction klaxon/appels : on gère, on ne punit pas'],
        q: 'Je devais klaxonner ?',
        r: 'Le klaxon sert à avertir d\'un danger imminent, pas à exprimer son droit. S\'il permet d\'éviter le choc (l\'autre peut encore s\'arrêter), oui. Sinon, toute l\'énergie va dans le freinage et la trajectoire.',
        verdict: 'Priorité cédée, accident évité : c\'est une réussite, pas une défaite.' },
      { time: 'Phase 4', speed: '70 → 40 km/h', title: 'AB6b — fin de priorité', emoji: '🔚',
        obs: ['Carré jaune barré : la bulle éclate immédiatement', 'Verbaliser : « fin de prioritaire → prochaine intersection = priorité à droite »', 'Réduction d\'allure anticipée AVANT le premier carrefour non protégé', 'Souvent couplé : entrée d\'agglomération = double changement de régime'],
        q: 'L\'AB6b est-il toujours suivi d\'un carrefour ?',
        r: 'Pas forcément tout de suite — mais le régime a déjà changé. L\'erreur classique : garder son allure « prioritaire » 500 m après le panneau et subir la première priorité à droite. Le panneau commande un changement de conduite immédiat.',
        verdict: 'Transition verbalisée + allure adaptée : l\'examinateur n\'attend rien d\'autre.' },
    ],
    pieges: [
      { t: 'Confondre AB2a et AB6a', d: 'AB2a (flèche) = prioritaire au PROCHAIN carrefour seulement. Le traiter comme un AB6a, c\'est se croire protégé sur des kilomètres qui ne le sont pas.', tag: 'Signalisation' },
      { t: 'Rater le AB6b', d: 'Après 10 min « protégé », le cerveau classe les intersections comme inoffensives. Le carré barré passe inaperçu… et la priorité à droite suivante punit cash.', tag: 'Transition' },
      { t: 'Défendre sa priorité', d: 'Maintenir l\'allure face à un véhicule qui s\'engage « pour lui apprendre ». Le constat te donnera raison, l\'hôpital aussi. On cède, toujours.', tag: 'Défense' },
    ],
    conduiteModele: <>Le conducteur modèle maintient <strong className="text-white">70 km/h stables</strong> sur l'axe prioritaire, scanne chaque débouché sans ralentir, cède sans hésiter quand un tiers force le passage, et change immédiatement de conduite au panneau AB6b en le verbalisant.</>,
    grille: [
      { l: 'Panneau AB6a identifié et verbalisé', c: 'Signalisation' },
      { l: 'Différence AB2a / AB6a connue', c: 'Signalisation' },
      { l: 'Allure stable maintenue sur l\'axe', c: 'Fluidité' },
      { l: 'Chaque débouché contrôlé du regard', c: 'Observation' },
      { l: 'Indices de refus de priorité détectés', c: 'Anticipation' },
      { l: 'Couverture du frein aux carrefours masqués', c: 'Adaptation' },
      { l: 'Cède face à un véhicule qui force', c: 'Défense' },
      { l: 'Piétons toujours prioritaires intégrés', c: 'Vulnérable' },
      { l: 'AB6b détecté immédiatement', c: 'Transition' },
      { l: 'Changement de régime verbalisé', c: 'Autonomie' },
      { l: 'Allure réduite avant le 1er carrefour suivant', c: 'Gestion allure' },
    ],
    phrase: '« La route prioritaire te prête un droit. Le panneau barré te le reprend — et il ne te préviendra qu\'une fois. »',
  },
};

const METHODE = [
  { k: 'S — Statut', d: 'Ce carrefour est-il protégé ? Par quoi ?' },
  { k: 'A — Allure', d: '30-40 si priorité à droite, stable si prioritaire' },
  { k: 'R — Regard', d: 'Droite d\'abord, puis balayage complet' },
  { k: 'C — Céder', d: 'Au moindre doute : l\'autre d\'abord' },
];

const CHECKLIST = [
  { t: 'Statut', d: 'à chaque carrefour, nommer le régime : protégé, priorité à droite, prioritaire.' },
  { t: 'AB1', d: 'croix de Saint-André = rappel priorité à droite, aucun droit accordé.' },
  { t: 'AB2a vs AB6a', d: 'ponctuel (1 carrefour) vs itinéraire (jusqu\'à AB6b).' },
  { t: 'Allure', d: '30-40 km/h en zone de priorités à droite, stable sur axe prioritaire.' },
  { t: 'Défense', d: 'ne jamais maintenir l\'allure face à un véhicule qui force.' },
  { t: 'Transition', d: 'AB6b ou entrée d\'agglo = changement de conduite immédiat et verbalisé.' },
];

const COMPARATIF = [
  { k: 'Panneau', pad: 'AB1 (croix) ou… rien du tout', prio: 'AB6a carré jaune · fin : AB6b barré' },
  { k: 'Qui passe ?', pad: 'Le véhicule venant de droite', prio: 'Toi, à toutes les intersections de l\'itinéraire' },
  { k: 'Allure type', pad: '30-40 km/h, pied prêt', prio: 'Stable (50-80 selon le lieu)' },
  { k: 'Regard clé', pad: 'La rue de droite, en premier', prio: 'Chaque débouché, sans ralentir' },
  { k: 'Durée de validité', pad: 'Carrefour par carrefour', prio: 'Jusqu\'au panneau AB6b' },
  { k: 'Piège d\'examen', pad: 'La rue « qui ne compte pas »', prio: 'Rater la fin de priorité' },
];

const CHECKLIST_ELEVE = [
  'Je nomme le régime de chaque carrefour avant de l\'aborder',
  'Rue à droite sans panneau = je cède, quelle que soit sa taille',
  'Je regarde la rue de droite EN PREMIER',
  'Sur axe prioritaire, je maintiens l\'allure mais pas les yeux fermés',
  'AB6b vu = je change de conduite immédiatement',
];

// ─── Schéma interactif SVG : carrefour vue du dessus ───
function SceneInteractive({ scenario, step, onStep }) {
  const data = SCENARIOS[scenario];
  const isPad = scenario === 'pad';
  // notre voiture monte depuis le bas ; en PAD elle cède au step 2 ; en prioritaire elle garde l'allure
  const carY = isPad ? [300, 250, 196, 110][step] : [300, 240, 185, 110][step];
  const carX = 215;
  // voiture adverse venant de la droite
  const rivalX = isPad ? [400, 360, 300, 150][step] : [400, 380, 310, 400][step];
  const showCones = step >= 1;
  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5" data-testid="intersections-scene">
      <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5">
        <Route size={13} className="text-red-400" /> Schéma interactif · {data.tab}
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="rounded-xl overflow-hidden bg-[#121318] border border-white/10 relative">
          <svg viewBox="0 0 440 340" className="w-full" style={{ maxHeight: 380 }} data-testid="intersections-svg">
            <rect x="0" y="0" width="440" height="340" fill="#15241a" />
            {/* route horizontale */}
            <rect x="0" y="80" width="440" height="80" fill="#2a2d33" />
            {/* route verticale (nous) */}
            <rect x="185" y="0" width="90" height="340" fill="#2a2d33" />
            <line x1="230" y1="170" x2="230" y2="340" stroke="#e8e8e8" strokeWidth="2" strokeDasharray="12 10" opacity="0.45" />
            <line x1="230" y1="0" x2="230" y2="70" stroke="#e8e8e8" strokeWidth="2" strokeDasharray="12 10" opacity="0.45" />
            {isPad ? (
              /* carrefour NON marqué + panneau AB1 */
              <g transform="translate(292, 190)">
                <line x1="0" y1="0" x2="0" y2="26" stroke="#888" strokeWidth="2.5" />
                <polygon points="0,-24 20,10 -20,10" fill="#fff" stroke="#d22" strokeWidth="4" />
                <path d="M -7 -8 L 7 4 M 7 -8 L -7 4" stroke="#111" strokeWidth="3" strokeLinecap="round" />
              </g>
            ) : (
              <>
                {/* marquage prioritaire : cédez sur la transversale + panneau AB6a */}
                <line x1="0" y1="120" x2="175" y2="120" stroke="#e8e8e8" strokeWidth="2.5" strokeDasharray="18 14" opacity="0.6" />
                <line x1="285" y1="120" x2="440" y2="120" stroke="#e8e8e8" strokeWidth="2.5" strokeDasharray="18 14" opacity="0.6" />
                {[0, 1, 2].map((i) => (
                  <polygon key={i} points={`282,${96 + i * 16} 282,${104 + i * 16} 294,${100 + i * 16}`} fill="#fff" />
                ))}
                <g transform="translate(150, 215)">
                  <line x1="0" y1="0" x2="0" y2="26" stroke="#888" strokeWidth="2.5" />
                  <rect x="-13" y="-13" width="26" height="26" rx="3" fill="#fff" transform={step === 3 ? 'rotate(45)' : 'rotate(45)'} />
                  <rect x="-7" y="-7" width="14" height="14" rx="1.5" fill="#f5c518" transform="rotate(45)" />
                  {step === 3 && <line x1="-14" y1="14" x2="14" y2="-14" stroke="#d22" strokeWidth="4" strokeLinecap="round" />}
                </g>
              </>
            )}
            {/* voiture adverse (droite) */}
            <g style={{ transform: `translate(${rivalX}px, 126px)`, transition: 'transform .8s ease' }} opacity={(!isPad && step === 3) ? 0 : 1}>
              <rect x="0" y="0" width="52" height="30" rx="7" fill="#5b7fd4" stroke="#fff" strokeWidth="1.5" />
              <rect x="10" y="5" width="12" height="20" rx="2" fill="#1a1a1e" />
            </g>
            {/* cônes de vision */}
            {showCones && (
              <g opacity="0.9">
                <polygon points={`230,${carY + 8} 430,96 430,155`} fill="rgba(255,196,0,0.14)" stroke="rgba(255,196,0,0.5)" strokeWidth="1" strokeDasharray="4 4" />
                <polygon points={`230,${carY + 8} 30,96 30,155`} fill="rgba(255,196,0,0.08)" stroke="rgba(255,196,0,0.3)" strokeWidth="1" strokeDasharray="4 4" />
              </g>
            )}
            {/* notre voiture */}
            <g style={{ transform: `translate(${carX}px, ${carY}px)`, transition: 'transform .8s cubic-bezier(.4,0,.2,1)' }}>
              <rect x="0" y="0" width="30" height="52" rx="7" fill="#dc2626" stroke="#fff" strokeWidth="1.5" />
              <rect x="5" y="8" width="20" height="10" rx="2" fill="#1a1a1e" />
            </g>
            {/* badge vitesse */}
            <g transform="translate(20, 300)">
              <rect x="0" y="-18" width="108" height="30" rx="8" fill="#000" opacity="0.65" />
              <text x="54" y="2" textAnchor="middle" fill="#fbbf24" fontSize="13" fontWeight="900" fontFamily="monospace">
                {isPad ? ['~45 km/h', '~35 km/h', 'ON CÈDE', '~30 km/h'][step] : ['70 km/h', '70 · regard', '70→40 !', 'FIN AB6b'][step]}
              </text>
            </g>
          </svg>
        </div>
        <div className="space-y-2">
          <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">4 étapes cliquables</div>
          {data.chapters.map((c, i) => (
            <button key={i} type="button" onClick={() => onStep(i)} data-testid={`intersections-step-${i}`}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${step === i ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
              <span className="w-8 shrink-0 text-center font-mono-spec text-[11px] font-bold text-red-300 bg-black/40 border border-white/10 rounded-md py-1">{i + 1}</span>
              <span className="flex-1">
                <span className="block text-white/90 text-[12.5px] font-bold leading-tight">{c.label}</span>
                <span className="block text-white/45 text-[11px] mt-0.5 leading-tight">{c.desc}</span>
              </span>
            </button>
          ))}
          <div className="rounded-xl bg-amber-950/20 border border-amber-700/30 p-3 mt-2">
            <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={11} /> Danger</div>
            <p className="text-white/70 text-[11.5px] leading-snug">{data.dangerNote}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeedCurve({ curve }) {
  const max = Math.max(...curve, 1);
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28" aria-hidden="true">
        {curve.map((v, i) => (
          <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-red-700 to-red-400 transition-all" style={{ height: `${Math.max(4, (v / max) * 100)}%`, opacity: 0.55 + 0.45 * (v / max) }} title={`${v} km/h`} />
        ))}
      </div>
      <div className="flex justify-between text-white/40 font-mono-spec text-[10px] mt-1.5">
        <span>approche</span><span>carrefour</span><span>décision</span><span>suite</span>
      </div>
    </div>
  );
}

function Quiz({ questions, storageKey }) {
  const [answers, setAnswers] = useState({});
  useEffect(() => { setAnswers({}); }, [storageKey]);
  const answered = Object.keys(answers).length;
  const score = questions.filter((qu, qi) => answers[qi] === qu.ok).length;
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="intersections-quiz">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <HelpCircle size={18} className="text-amber-400" /> QCM — valide tes règles
        </h3>
        <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${answered === questions.length ? (score >= 5 ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-amber-950/30 border-amber-600/40 text-amber-300') : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="intersections-quiz-score">
          {answered === questions.length ? `${score}/${questions.length} bonnes réponses` : `${answered}/${questions.length} répondues`}
        </span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {questions.map((qu, qi) => {
          const picked = answers[qi];
          return (
            <div key={qi} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="text-white text-[13px] font-bold leading-snug">Q{qi + 1} : {qu.q}</div>
                <span className="shrink-0 w-6 h-6 rounded-md bg-black/40 border border-white/10 text-white/50 font-mono-spec text-[10px] font-bold flex items-center justify-center">0{qi + 1}</span>
              </div>
              <div className="space-y-1.5">
                {qu.opts.map((o, oi) => {
                  const isPicked = picked === oi;
                  const showState = picked !== undefined;
                  const good = oi === qu.ok;
                  return (
                    <button key={oi} type="button" disabled={showState} data-testid={`intersections-quiz-q${qi}-o${oi}`}
                      onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`w-full flex items-start gap-2 rounded-lg border px-2.5 py-2 text-left text-[12px] leading-snug transition ${
                        showState && good ? 'border-emerald-600/50 bg-emerald-950/30 text-emerald-200'
                        : showState && isPicked ? 'border-red-600/50 bg-red-950/30 text-red-300'
                        : showState ? 'border-white/5 bg-transparent text-white/35'
                        : 'border-white/10 bg-white/[0.02] text-white/75 hover:bg-white/[0.06]'}`}>
                      <span className="shrink-0 w-[18px] h-[18px] rounded-full border border-current flex items-center justify-center text-[9.5px] font-bold">{String.fromCharCode(65 + oi)}</span>
                      {o}
                    </button>
                  );
                })}
              </div>
              {picked !== undefined && (
                <p className={`mt-2.5 text-[11.5px] leading-snug ${picked === qu.ok ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {picked === qu.ok ? '✓ Bonne réponse. ' : '✗ Pas tout à fait. '}{qu.exp}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistEleve() {
  const KEY = 'cv-intersections-checklist-eleve';
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
  });
  const n = CHECKLIST_ELEVE.filter((_, i) => done[i]).length;
  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="intersections-checklist-eleve">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-400" /> Check-list élève — avant ta prochaine leçon
        </h3>
        <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${n === CHECKLIST_ELEVE.length ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="intersections-checklist-score">{n}/{CHECKLIST_ELEVE.length} validés</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {CHECKLIST_ELEVE.map((c, i) => (
          <button key={i} type="button" onClick={() => toggle(i)} data-testid={`intersections-checklist-${i}`}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${done[i] ? 'border-emerald-600/40 bg-emerald-950/20' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${done[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25'}`}>
              {done[i] && <CheckCircle2 size={14} className="text-black" />}
            </span>
            <span className={`text-[13px] leading-snug ${done[i] ? 'text-white' : 'text-white/70'}`}>{c}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function IntersectionsPrioritePage() {
  const [scen, setScen] = useState('pad');
  const [step, setStep] = useState(0);
  const [seg, setSeg] = useState(0);
  const [checked, setChecked] = useState({});
  const [showCorrection, setShowCorrection] = useState(false);
  const data = SCENARIOS[scen];
  const lsKey = `cv-intersections-grille-${scen}`;

  useEffect(() => {
    try { setChecked(JSON.parse(localStorage.getItem(lsKey)) || {}); } catch { setChecked({}); }
    setSeg(0);
    setStep(0);
  }, [lsKey]);

  const nbChecked = useMemo(() => data.grille.filter((_, i) => checked[i]).length, [checked, data]);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem(lsKey, JSON.stringify(next));
  };
  const reset = () => { setChecked({}); localStorage.removeItem(lsKey); };

  const s = data.segments[seg];

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12 pb-16" data-testid="intersections-page">
        <FadeUp>
          <SectionTitle
            kicker="Cours interactif · REMC C2"
            title="Intersections & priorité à droite, la règle silencieuse."
            sub="Un cours interactif construit par un moniteur ECSR : carrefour animé étape par étape, priorité à droite vs route prioritaire, pièges d'examen, QCM et grille de révision à cocher."
          />
        </FadeUp>

        {/* Onglets scénarios */}
        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(SCENARIOS).map(([key, v]) => {
            const Icon = v.icon;
            return (
              <button key={key} type="button" onClick={() => setScen(key)} data-testid={`intersections-tab-${key}`}
                className={`h-11 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 border transition ${
                  scen === key ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-900/40' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'
                }`}>
                <Icon size={14} /> {v.tab}
              </button>
            );
          })}
        </div>

        {/* Schéma interactif */}
        <SceneInteractive scenario={scen} step={step} onStep={setStep} />

        {/* Hero fiche */}
        <div className="mt-5 grid lg:grid-cols-[1.5fr_1fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-3">
              <Clock size={11} /> {data.badge}
            </div>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] leading-tight">{data.titre}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.identite.chips.map((c, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[11.5px]">{c}</span>
              ))}
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {[
                { k: 'Type d\'intersection', v: data.identite.type, d: data.identite.typeDesc, cl: 'border-white/10' },
                { k: 'Difficulté', v: data.identite.difficulte, d: data.identite.difficulteDesc, cl: 'border-amber-600/30' },
                { k: 'Danger principal', v: data.identite.danger, d: data.identite.dangerDesc, cl: 'border-red-700/40' },
              ].map((c, i) => (
                <div key={i} className={`rounded-xl bg-white/[0.03] border ${c.cl} p-3.5`}>
                  <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">{c.k}</div>
                  <div className="text-white text-[13.5px] font-bold mt-1 leading-snug">{c.v}</div>
                  <div className="text-white/50 text-[11.5px] mt-1 leading-snug">{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-red-900/40 bg-gradient-to-br from-[#1a0b0d] to-[#0d0d0e] p-5 sm:p-6 flex flex-col">
            <div className="text-white/45 text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Gauge size={12} className="text-red-400" /> Courbe d'allure type</div>
            <SpeedCurve curve={data.curve} />
            <div className="mt-4 grid grid-cols-3 gap-2">
              {data.stats.map((st, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/10 p-2.5 text-center">
                  <div className="font-serif-editorial text-red-400 text-[20px] leading-none">{st.v}</div>
                  <div className="text-white/50 text-[9.5px] uppercase tracking-wider font-mono-spec mt-1">{st.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-3.5 text-[12.5px] text-white/75 leading-relaxed">
              <div className="text-red-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5">Ce que doit comprendre l'élève</div>
              {data.comprendre}
              <div className="mt-2.5 flex gap-2">
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">Allure {data.vitesseLegale}</span>
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">{data.rapport}</span>
              </div>
              <div className="text-white/35 font-mono-spec text-[10px] mt-2.5">{data.remc}</div>
            </div>
          </div>
        </div>

        {/* Focus pédagogique */}
        <div className="mt-5 rounded-2xl border border-white/15 bg-gradient-to-r from-[#15151a] to-[#0d0d0e] p-4 sm:p-5 flex items-start gap-3" data-testid="intersections-focus">
          <span className="shrink-0 w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center"><AlertTriangle size={16} /></span>
          <div>
            <p className="text-white/80 text-[13px] leading-relaxed"><strong className="text-amber-300">Focus pédagogique :</strong> {data.focus.txt}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.focus.tags.map((t, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/60 text-[10.5px] font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline interactive */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px]">Timeline pédagogique interactive</h3>
            <span className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec font-bold">4 phases cliquables</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {data.segments.map((sg, i) => (
              <button key={i} type="button" onClick={() => setSeg(i)} data-testid={`intersections-seg-${i}`}
                className={`rounded-xl border p-3 text-left transition ${seg === i ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
                <div className="font-mono-spec text-[10.5px] text-white/50">{sg.time}</div>
                <div className={`text-[13px] font-bold mt-1 leading-tight ${seg === i ? 'text-red-300' : 'text-white/85'}`}>{sg.emoji} {sg.title}</div>
                <div className="font-mono-spec text-[10.5px] text-white/45 mt-1">{sg.speed}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid lg:grid-cols-[1.3fr_1fr] gap-4">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 sm:p-5">
              <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Eye size={12} className="text-red-400" /> Ce qu'on observe · {s.time}</div>
              <ul className="space-y-2">
                {s.obs.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13px] leading-snug">
                    <ChevronRight size={14} className="text-red-400 mt-0.5 shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
                <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5 inline-flex items-center gap-1.5"><HelpCircle size={12} /> Question moniteur</div>
                <div className="text-white text-[13.5px] font-bold">{s.q}</div>
                <p className="text-white/70 text-[12.5px] mt-2 leading-relaxed">{s.r}</p>
              </div>
              <div className="rounded-xl bg-emerald-950/15 border border-emerald-700/30 p-3.5">
                <div className="text-emerald-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Verdict</div>
                <p className="text-white/80 text-[12.5px] leading-snug">{s.verdict}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pièges */}
        <div className="mt-8">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] mb-4 inline-flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> Les 3 pièges d'examen</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.pieges.map((p, i) => (
              <div key={i} className="rounded-xl border border-red-900/40 bg-[#120a0b] p-4">
                <div className="w-7 h-7 rounded-lg bg-red-600 text-white font-mono-spec text-[12px] font-bold flex items-center justify-center mb-3">{String(i + 1).padStart(2, '0')}</div>
                <div className="text-white text-[13.5px] font-bold leading-snug">{p.t}</div>
                <p className="text-white/60 text-[12px] mt-1.5 leading-relaxed">{p.d}</p>
                <span className="inline-block mt-2.5 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider">{p.tag}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-4 text-[12.5px] text-white/70 leading-relaxed">
            <strong className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-widest block mb-1">Le conducteur modèle</strong>
            {data.conduiteModele}
          </div>
        </div>

        {/* Comparatif */}
        <div className="mt-8 rounded-2xl border border-blue-900/50 bg-gradient-to-br from-[#0e1526] to-[#0d0d0e] p-5 sm:p-7" data-testid="intersections-comparatif">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2 mb-4"><Scale size={18} className="text-blue-300" /> Priorité à droite vs Route prioritaire</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[560px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-2.5 pr-4 text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold w-[26%]">Critère</th>
                  <th className="py-2.5 pr-4 text-red-300 text-[12px] font-black uppercase tracking-wider">✖️ Priorité à droite</th>
                  <th className="py-2.5 text-amber-300 text-[12px] font-black uppercase tracking-wider">🟨 Route prioritaire</th>
                </tr>
              </thead>
              <tbody>
                {COMPARATIF.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-3 pr-4 text-white/55 text-[12px] font-semibold">{r.k}</td>
                    <td className="py-3 pr-4 text-white/85 text-[12.5px] leading-snug">{r.pad}</td>
                    <td className="py-3 text-white/85 text-[12.5px] leading-snug">{r.prio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* QCM */}
        <Quiz questions={data.quiz} storageKey={scen} />

        {/* Grille de révision */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" id="grille">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
              <ClipboardCheck size={18} className="text-red-400" /> Grille de révision à cocher
            </h3>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${nbChecked >= 8 ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="intersections-grille-score">
                {nbChecked}/{data.grille.length} validés
              </span>
              <button type="button" onClick={() => setShowCorrection(!showCorrection)} data-testid="intersections-grille-correction"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                <Eye size={13} /> {showCorrection ? 'Masquer' : 'Voir'} correction moniteur
              </button>
              <button type="button" onClick={reset} data-testid="intersections-grille-reset"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 text-[12px] font-semibold inline-flex items-center gap-1.5">
                <RotateCcw size={13} /> Réinitialiser
              </button>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {data.grille.map((g, i) => (
              <button key={i} type="button" onClick={() => toggle(i)} data-testid={`intersections-critere-${i}`}
                className="w-full flex items-center gap-3 py-3 text-left group">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${checked[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25 group-hover:border-white/50'}`}>
                  {checked[i] && <CheckCircle2 size={14} className="text-black" />}
                </span>
                <span className={`flex-1 text-[13.5px] leading-snug transition ${checked[i] ? 'text-white' : 'text-white/70'}`}>{g.l}</span>
                {showCorrection && <span className="hidden sm:block text-white/40 text-[11px] italic shrink-0">{g.c} · cocher si validé</span>}
              </button>
            ))}
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#101b33] border border-blue-900/50 p-4">
              <div className="text-blue-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Seuil de validation</div>
              <p className="text-white/85 text-[12.5px] leading-snug">8/{data.grille.length} = compétence acquise en autonomie. En dessous = refaire avec verbalisation à voix haute.</p>
            </div>
            <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
              <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><Quote size={11} /> Phrase clé moniteur</div>
              <p className="text-white text-[13px] font-serif-editorial leading-snug">{data.phrase}</p>
            </div>
          </div>
        </div>

        {/* Check-list élève */}
        <ChecklistEleve />

        {/* Méthode SARC + checklist examen */}
        <div className="mt-8 grid lg:grid-cols-[1fr_1.3fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#14090b] to-[#0d0d0e] p-5 sm:p-6">
            <div className="text-red-300 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-2">Méthode S.A.R.C — Toutes intersections</div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">Statut, Allure,<br />Regard, Céder.</h3>
            <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed">Méthode à réciter à l'oral avant chaque carrefour non protégé.</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {METHODE.map((p, i) => (
                <div key={i} className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
                  <div className="text-red-300 font-mono-spec text-[11px] font-bold uppercase tracking-wider">{p.k}</div>
                  <div className="text-white/65 text-[11.5px] mt-1 leading-snug">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6">
            <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><ListChecks size={13} className="text-red-400" /> Check-list examen — intersections & priorités</div>
            <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="text-[12.5px] text-white/70 leading-snug">
                  <strong className="text-white">{c.t}</strong> : {c.d}
                </li>
              ))}
            </ul>
            <div className="mt-4 inline-flex px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-white/55 font-mono-spec text-[10.5px]">Support : {data.badge}</div>
            <p className="text-white/35 text-[11px] mt-3">Fiche utilisable en salle, en voiture ou en autonomie · Sans impression · Code Voiture × moniteurs ECSR</p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
